/**
 * First-run tour for the fullscreen / expanded map: a short sequence of
 * callouts on the toolbar tools used to build a briefing product. Shown once
 * the first time the analyst enters fullscreen; they can skip it permanently.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { hudPortalContainer } from './PathfinderHudKit';
import { createPortal } from 'react-dom';

const STORAGE_KEY = 'pf.fullscreen-product-tour.v1';

export function isProductTourDone(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function markProductTourDone(): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    /* private mode */
  }
}

/** Clear the flag so the tour can run again (e.g. a "Replay tour" control). */
export function resetProductTour(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

type Step = {
  id: string;
  title: string;
  body: string;
  /** `data-tour` value on the toolbar control. Null = welcome card, no spotlight. */
  target: string | null;
};

const STEPS: Step[] = [
  {
    id: 'welcome',
    title: 'Build a product from this view',
    body: 'Fullscreen is the briefing workspace: numbered timeline, Street View callouts, dwell insets, then one export of the whole view. A few steps, or skip if you already know it.',
    target: null,
  },
  {
    id: 'story',
    title: 'Story timeline',
    body: 'Open the Story panel for the numbered sequence (dwells, travel, cameras). Numbers match the badges on the map. Crop to the current view; generate an AI summary from the same list.',
    target: 'story',
  },
  {
    id: 'cameras',
    title: 'Cameras',
    body: 'Pin Street View cards for cameras the track passed, show the in-view strip, and fade the cards so the map stays readable. Pinning also opens the Story timeline so numbering stays consistent.',
    target: 'cameras',
  },
  {
    id: 'insets',
    title: 'Dwell insets',
    body: 'Drop a mini-map on a dwell for a close-up you can drag, resize, and switch to imagery. Use these when badges stack at the same intersection.',
    target: 'insets',
  },
  {
    id: 'export',
    title: 'Export the product',
    body: 'One capture of everything on screen (map, badges, callouts, insets, HUD) plus the data behind the widgets. HTML or PDF for the briefing; PNG for the view alone.',
    target: 'export',
  },
];

export interface FullscreenProductTourProps {
  /** Tour only runs while the map is fullscreen. */
  active: boolean;
  /** Increment to start (or restart) the tour even after it was skipped. */
  replayKey?: number;
  /** Expand the toolbar so the spotlight targets exist. */
  onEnsureToolbar: () => void;
}

type Pos = { top: number; left: number; width: number; height: number };

const FullscreenProductTour: React.FC<FullscreenProductTourProps> = ({ active, replayKey = 0, onEnsureToolbar }) => {
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);
  const [spot, setSpot] = useState<Pos | null>(null);

  useEffect(() => {
    if (!active) {
      setOpen(false);
      return;
    }
    const replay = replayKey > 0;
    if (!replay && isProductTourDone()) {
      setOpen(false);
      return;
    }
    onEnsureToolbar();
    const t = window.setTimeout(() => {
      setStep(0);
      setOpen(true);
    }, replay ? 80 : 350);
    return () => window.clearTimeout(t);
  }, [active, replayKey, onEnsureToolbar]);

  const available = useMemo(() => {
    if (typeof document === 'undefined') return STEPS;
    return STEPS.filter((s) => !s.target || document.querySelector(`[data-tour="${s.target}"]`));
  }, [open, active, step]);

  const current = available[Math.min(step, available.length - 1)];

  useEffect(() => {
    if (!open || !current) {
      setSpot(null);
      return;
    }
    const measure = () => {
      if (!current.target) {
        setSpot(null);
        return;
      }
      const el = document.querySelector(`[data-tour="${current.target}"]`) as HTMLElement | null;
      if (!el) {
        setSpot(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setSpot({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    window.addEventListener('resize', measure);
    document.addEventListener('fullscreenchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      document.removeEventListener('fullscreenchange', measure);
    };
  }, [open, current]);

  const finish = () => {
    markProductTourDone();
    setOpen(false);
  };

  if (!open || !current || available.length === 0) return null;

  const last = step >= available.length - 1;
  const cardW = 320;
  const host = hudPortalContainer();
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;

  let cardTop = Math.max(24, vh / 2 - 90);
  let cardLeft = 48;
  if (spot) {
    cardTop = Math.min(Math.max(16, spot.top + spot.height / 2 - 70), vh - 220);
    cardLeft = Math.max(16, spot.left - cardW - 20);
  }

  const node = (
    <Box
      data-export-hide
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 4500,
        pointerEvents: 'none',
      }}
    >
      {!spot && <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.45)', pointerEvents: 'auto' }} />}
      {spot && <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'auto' }} />}
      {spot && (
        <Box
          sx={{
            position: 'fixed',
            top: spot.top - 6,
            left: spot.left - 6,
            width: spot.width + 12,
            height: spot.height + 12,
            borderRadius: 1.5,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
            border: '2px solid #4fc3f7',
            pointerEvents: 'none',
          }}
        />
      )}
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          top: cardTop,
          left: cardLeft,
          width: cardW,
          maxWidth: `min(320px, ${vw - 32}px)`,
          pointerEvents: 'auto',
          bgcolor: 'rgba(22,24,30,0.98)',
          color: '#e6e8ec',
          border: '1px solid rgba(255,255,255,0.14)',
          p: 1.75,
        }}
      >
        <Typography variant="overline" sx={{ color: 'rgba(230,232,236,0.55)', letterSpacing: '0.12em' }}>
          Product tour · {step + 1} of {available.length}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, mt: 0.25, lineHeight: 1.25 }}>
          {current.title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(230,232,236,0.78)', mt: 0.75, lineHeight: 1.45 }}>
          {current.body}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
          <Button size="small" onClick={finish} sx={{ color: 'rgba(230,232,236,0.65)', textTransform: 'none' }}>
            I&apos;m good — skip
          </Button>
          <Box sx={{ flex: 1 }} />
          {step > 0 && (
            <Button size="small" onClick={() => setStep((s) => s - 1)} sx={{ color: '#e6e8ec', textTransform: 'none' }}>
              Back
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            onClick={() => (last ? finish() : setStep((s) => s + 1))}
            sx={{ textTransform: 'none', bgcolor: '#4fc3f7', color: '#111', '&:hover': { bgcolor: '#81d4fa' } }}
          >
            {last ? 'Done' : 'Next'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );

  return createPortal(node, host);
};

export default FullscreenProductTour;
